import type { ComponentChildren, JSX } from 'preact';
import { Icon, type IconName } from './Icon';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'success';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends Omit<JSX.ButtonHTMLAttributes<HTMLButtonElement>, 'size'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  iconLeft?: IconName;
  block?: boolean;
  children?: ComponentChildren;
}

/** The primary interactive control. Keyboard-accessible by default (native <button>), with a visible focus ring and a loading state that keeps the label for screen readers. */
export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  iconLeft,
  block = false,
  disabled,
  class: className,
  children,
  ...rest
}: ButtonProps) {
  const classes = [
    'c-btn',
    variant !== 'primary' ? `c-btn--${variant}` : '',
    size !== 'md' ? `c-btn--${size}` : '',
    block ? 'c-btn--block' : '',
    loading ? 'c-btn--loading' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button class={classes} disabled={disabled || loading} aria-busy={loading || undefined} {...rest}>
      {loading ? <Icon name="refresh" size={16} class="c-btn__spinner" /> : iconLeft ? <Icon name={iconLeft} size={16} /> : null}
      <span>{children}</span>
    </button>
  );
}
